import { useState, useEffect, useMemo } from 'react';
import {
    Pane, Heading, Text, Button, Textarea, SelectMenu,
    Spinner, toaster, majorScale, Card, EditIcon, ClipboardIcon, TrashIcon,
} from 'evergreen-ui';
import { useAuth } from '../services/authContext';
import { getNotesByGuest, addNote, updateNote, deleteNote } from '../services/guestNotesService';
import '../styles/responsive.css';
import { useRole } from '../contexts/RoleContext';

const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr.replace(' ', 'T'));
    return d.toLocaleString('it-IT', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const GuestNotes = ({ guests }) => {
    const { effectiveIsDirector: isDirector } = useRole();
    const { user } = useAuth();
    const [selectedGuestId, setSelectedGuestId] = useState(null);
    const [selectedGuestLabel, setSelectedGuestLabel] = useState('Seleziona un ospite');
    const [notes, setNotes] = useState([]);
    const [loadingNotes, setLoadingNotes] = useState(false);
    const [newNoteText, setNewNoteText] = useState('');
    const [saving, setSaving] = useState(false);
    const [editingNoteId, setEditingNoteId] = useState(null);
    const [editingText, setEditingText] = useState('');
    const [savingEdit, setSavingEdit] = useState(false);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);

    const guestItems = useMemo(() => (guests || []).map((g) => ({
        label: `${g.nome} ${g.cognome}`,
        value: String(g.id),
    })), [guests]);

    useEffect(() => {
        if (!selectedGuestId) return;
        setLoadingNotes(true);
        getNotesByGuest(selectedGuestId)
            .then((res) => {
                if (res.success) setNotes(res.data || []);
                else toaster.danger('Errore nel caricamento delle note');
            })
            .catch(() => toaster.danger('Errore nel caricamento delle note'))
            .finally(() => setLoadingNotes(false));
    }, [selectedGuestId]);

    const handleSelectGuest = (item) => {
        setSelectedGuestId(parseInt(item.value, 10));
        setSelectedGuestLabel(item.label);
        setNotes([]);
        setNewNoteText('');
        setEditingNoteId(null);
    };

    const handleAddNote = async () => {
        if (!newNoteText.trim()) {
            toaster.warning('Scrivi una nota prima di salvare');
            return;
        }
        setSaving(true);
        try {
            const res = await addNote(selectedGuestId, newNoteText.trim());
            if (res.success) {
                const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
                setNotes((prev) => [{
                    id: res.id,
                    guest_id: selectedGuestId,
                    user_id: user.id,
                    testo: newNoteText.trim(),
                    created_at: now,
                    updated_at: null,
                    user_nome: user.nome,
                    user_cognome: user.cognome,
                }, ...prev]);
                setNewNoteText('');
                toaster.success('Nota salvata');
            } else {
                toaster.danger(res.error || 'Errore nel salvataggio');
            }
        } catch {
            toaster.danger('Errore nel salvataggio della nota');
        }
        setSaving(false);
    };

    const handleStartEdit = (note) => {
        setEditingNoteId(note.id);
        setEditingText(note.testo);
    };

    const handleCancelEdit = () => {
        setEditingNoteId(null);
        setEditingText('');
    };

    const handleSaveEdit = async (noteId) => {
        if (!editingText.trim()) {
            toaster.warning('Il testo non può essere vuoto');
            return;
        }
        setSavingEdit(true);
        try {
            const res = await updateNote(noteId, editingText.trim());
            if (res.success) {
                const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
                setNotes((prev) => prev.map((n) =>
                    n.id === noteId ? { ...n, testo: editingText.trim(), updated_at: now } : n
                ));
                setEditingNoteId(null);
                setEditingText('');
                toaster.success('Nota aggiornata');
            } else {
                toaster.danger(res.error || "Errore nell'aggiornamento");
            }
        } catch {
            toaster.danger("Errore nell'aggiornamento della nota");
        }
        setSavingEdit(false);
    };

    const handleCopy = (testo) => {
        navigator.clipboard.writeText(testo)
            .then(() => toaster.success('Nota copiata negli appunti'))
            .catch(() => toaster.danger('Errore nella copia'));
    };

    const handleCopyAll = () => {
        const testo = notes.map((n) => {
            const autore = `${n.user_nome} ${n.user_cognome}`;
            const data = formatDateTime(n.created_at);
            const mod = n.updated_at ? ` (mod. ${formatDateTime(n.updated_at)})` : '';
            return `[${autore} — ${data}${mod}]\n${n.testo}`;
        }).join('\n\n---\n\n');
        navigator.clipboard.writeText(testo)
            .then(() => toaster.success('Tutte le note copiate negli appunti'))
            .catch(() => toaster.danger('Errore nella copia'));
    };

    const handleDeleteNote = async (noteId) => {
        try {
            const res = await deleteNote(noteId);
            if (res.success) {
                setNotes((prev) => prev.filter((n) => n.id !== noteId));
                toaster.success('Nota eliminata');
            } else {
                toaster.danger(res.error || "Errore nell'eliminazione");
            }
        } catch {
            toaster.danger("Errore nell'eliminazione della nota");
        }
        setConfirmDeleteId(null);
    };

    return (
        <Pane marginTop={majorScale(4)}>
            <Heading size={600} marginBottom={12}>Note</Heading>

            <Pane display="flex" alignItems="center" gap={12} marginBottom={16} className="guest-select-row">
                <SelectMenu
                    title="Seleziona ospite"
                    options={guestItems}
                    selected={selectedGuestId ? String(selectedGuestId) : null}
                    onSelect={handleSelectGuest}
                    width={300}
                >
                    <Button className="guest-select-btn">{selectedGuestLabel}</Button>
                </SelectMenu>
                {loadingNotes && <Spinner size={20} />}
            </Pane>

            {selectedGuestId && (
                <>
                    {!loadingNotes && notes.length === 0 && (
                        <Text color="muted" display="block" marginBottom={majorScale(2)}>
                            Nessuna nota presente per questo ospite.
                        </Text>
                    )}

                    {isDirector && notes.length > 0 && (
                        <Pane display="flex" justifyContent="flex-end" marginBottom={12}>
                            <Button onClick={handleCopyAll}>
                                Copia tutte <ClipboardIcon marginLeft={4} />
                            </Button>
                        </Pane>
                    )}

                    {notes.map((note) => (
                        <Card
                            key={note.id}
                            elevation={1}
                            padding={16}
                            marginBottom={12}
                            background="white"
                        >
                            <Pane display="flex" justifyContent="space-between" alignItems="flex-start" marginBottom={8} flexWrap="wrap" gap={8} className="card-header">
                                <Pane>
                                    <Text fontWeight={600}>{note.user_nome} {note.user_cognome}</Text>
                                    <Text color="muted" marginLeft={8} fontSize={12}>
                                        {formatDateTime(note.created_at)}
                                    </Text>
                                    {note.updated_at && (
                                        <Text color="muted" marginLeft={8} fontSize={12}>
                                            (mod. {formatDateTime(note.updated_at)})
                                        </Text>
                                    )}
                                </Pane>
                                <Pane display="flex" gap={8} flexWrap="wrap" className="card-actions">
                                    {parseInt(note.user_id, 10) === parseInt(user?.id, 10) && editingNoteId !== note.id && confirmDeleteId !== note.id && (
                                        <Button size="small" onClick={() => handleStartEdit(note)}>
                                            Modifica <EditIcon marginLeft={4} />
                                        </Button>
                                    )}
                                    {isDirector && confirmDeleteId !== note.id && (
                                        <Button size="small" onClick={() => handleCopy(note.testo)}>
                                            Copia <ClipboardIcon marginLeft={4} />
                                        </Button>
                                    )}
                                    {isDirector && confirmDeleteId === note.id ? (
                                        <>
                                            <Button size="small" intent="danger" appearance="primary" onClick={() => handleDeleteNote(note.id)}>
                                                Conferma eliminazione
                                            </Button>
                                            <Button size="small" onClick={() => setConfirmDeleteId(null)}>
                                                Annulla
                                            </Button>
                                        </>
                                    ) : isDirector && (
                                        <Button size="small" intent="danger" onClick={() => setConfirmDeleteId(note.id)}>
                                            <TrashIcon />
                                        </Button>
                                    )}
                                </Pane>
                            </Pane>

                            {editingNoteId === note.id ? (
                                <Pane>
                                    <Textarea
                                        value={editingText}
                                        onChange={(e) => setEditingText(e.target.value)}
                                        minHeight={100}
                                    />
                                    <Pane display="flex" gap={8} marginTop={8} justifyContent="flex-end">
                                        <Button onClick={handleCancelEdit}>Annulla</Button>
                                        <Button
                                            appearance="primary"
                                            intent="success"
                                            isLoading={savingEdit}
                                            onClick={() => handleSaveEdit(note.id)}
                                        >
                                            Salva
                                        </Button>
                                    </Pane>
                                </Pane>
                            ) : (
                                <Text whiteSpace="pre-wrap">{note.testo}</Text>
                            )}
                        </Card>
                    ))}

                    <Pane marginTop={majorScale(2)}>
                        <Text fontWeight={600} display="block" marginBottom={8}>Aggiungi nota</Text>
                        <Textarea
                            placeholder="Scrivi una nota..."
                            value={newNoteText}
                            onChange={(e) => setNewNoteText(e.target.value)}
                            minHeight={120}
                        />
                        <Pane display="flex" justifyContent="flex-end" marginTop={8}>
                            <Button appearance="primary" intent="success" isLoading={saving} onClick={handleAddNote}>
                                Salva nota
                            </Button>
                        </Pane>
                    </Pane>
                </>
            )}
        </Pane>
    );
};

export default GuestNotes;
